import React, { useState, useEffect } from 'react';
import { useCookieConsent, ConsentCategory } from '../contexts/CookieConsentContext';
import { useTranslation } from '../hooks/useTranslation';

interface CookieSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CategoryInfo {
  key: ConsentCategory;
  required: boolean;
  defaultValue: boolean;
}

const COOKIE_CATEGORIES: CategoryInfo[] = [
  { key: 'necessary', required: true, defaultValue: true },
  { key: 'functional', required: false, defaultValue: false },
  { key: 'analytics', required: false, defaultValue: false },
  { key: 'marketing', required: false, defaultValue: false }
];

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { consent, updateConsent, hasConsent } = useCookieConsent();
  
  // Local state for modal settings
  const [settings, setSettings] = useState<Record<ConsentCategory, boolean>>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });

  // Initialize settings from current consent
  useEffect(() => {
    if (consent) {
      setSettings({
        necessary: consent.necessary,
        functional: consent.functional,
        analytics: consent.analytics,
        marketing: consent.marketing
      });
    } else {
      // Default to current consent state or false
      setSettings({
        necessary: true,
        functional: hasConsent('functional'),
        analytics: hasConsent('analytics'),
        marketing: hasConsent('marketing')
      });
    }
  }, [consent, hasConsent]);

  const handleToggle = (category: ConsentCategory) => {
    // Can't toggle necessary cookies
    if (category === 'necessary') return;
    
    setSettings(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSaveSettings = () => {
    updateConsent({
      necessary: true, // Always true
      functional: settings.functional,
      analytics: settings.analytics,
      marketing: settings.marketing
    });
    onClose();
  };

  const handleAcceptAll = () => {
    setSettings({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });
    updateConsent({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });
    onClose();
  };

  const handleRejectAll = () => {
    setSettings({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
    updateConsent({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">
            {t('gdpr.settings.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <p className="text-gray-300 mb-6 leading-relaxed">
            {t('gdpr.settings.description')}
          </p>

          {/* Cookie Categories */}
          <div className="space-y-6">
            {COOKIE_CATEGORIES.map((category) => (
              <div key={category.key} className="border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-medium text-white mb-1">
                      {t(`gdpr.categories.${category.key}.title`)}
                      {category.required && (
                        <span className="ml-2 text-xs text-gray-400 font-normal">
                          ({t('gdpr.categories.always_active')})
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {t(`gdpr.categories.${category.key}.description`)}
                    </p>
                  </div>
                  
                  <div className="ml-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings[category.key]}
                        onChange={() => handleToggle(category.key)}
                        disabled={category.required}
                        className="sr-only"
                      />
                      <div className={`
                        w-11 h-6 rounded-full transition-colors duration-200
                        ${settings[category.key] 
                          ? 'bg-purple-600' 
                          : 'bg-gray-600'
                        }
                        ${category.required ? 'opacity-75' : 'hover:bg-opacity-80'}
                      `}>
                        <div className={`
                          w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200
                          ${settings[category.key] ? 'translate-x-5' : 'translate-x-0'}
                          mt-0.5 ml-0.5
                        `} />
                      </div>
                    </label>
                  </div>
                </div>

                {/* Additional info for some categories */}
                {category.key === 'analytics' && settings[category.key] && (
                  <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-900/50 rounded">
                    {t('gdpr.categories.analytics.details')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-gray-700 bg-gray-900/50">
          <button
            onClick={handleRejectAll}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            {t('gdpr.settings.reject_all')}
          </button>
          
          <div className="flex gap-3 sm:ml-auto">
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {t('gdpr.settings.accept_all')}
            </button>
            <button
              onClick={handleSaveSettings}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
            >
              {t('gdpr.settings.save_preferences')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;